// src/pages/SoloRoomPage.tsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import {
  useAudioPlayback,
  usePhaseCues,
  useOfflinePresets,
  useContentIndex,
} from "../hooks";
import { isValidPreset } from "../hooks/useContentIndex";
import { COUNTDOWN_DURATION_MS, getAudioUrl } from "../utils/constants";
import { formatSeconds } from "../utils/helpers";
import { getPhaseText } from "../utils/phaseCues";
import { BreathingCircle } from "../components/BreathingCircle";
import { OfflineIcon } from "../components/Icons";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { TopBar } from "../components/TopBar";
import { PageFooter } from "../components/PageFooter";

type RoomStatus = "idle" | "countdown" | "playing";

export function SoloRoomPage() {
  const navigate = useNavigate();
  const { presetId: presetIdParam } = useParams<{ presetId: string }>();
  const { language } = useAppContext();
  const { soloPresets, isLoading: isLoadingPresets } = useContentIndex();

  // Validate preset ID
  const validPresetId = isValidPreset(presetIdParam, soloPresets)
    ? presetIdParam
    : null;

  const [status, setStatus] = useState<RoomStatus>("idle");
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [hasAudioEnded, setHasAudioEnded] = useState(false);
  const audioDidPlayRef = useRef(false);

  const audioUrl = validPresetId ? getAudioUrl(validPresetId) : null;

  const {
    isLoaded,
    isPlaying,
    isPaused,
    remainingTime,
    unlockAudio,
    schedulePlayback,
    pausePlayback,
    resumePlayback,
    stopPlayback,
    getCurrentTime,
  } = useAudioPlayback(audioUrl, {
    presetId: validPresetId,
    language,
    lockScreenControls: true,
  });

  // Phase cues for displaying Breathe/Pause/Hold (keep active during pause)
  const { currentPhase, phaseRemaining, authorUrl, title, titleRu } =
    usePhaseCues(audioUrl, getCurrentTime, isPlaying || isPaused);

  // Preset title from the same cue file usePhaseCues already loads
  const presetTitle = title
    ? language === "ru"
      ? titleRu || title
      : title
    : "";

  // Offline status
  const { isPresetCached, cachePreset } = useOfflinePresets();
  const isCurrentPresetCached = validPresetId
    ? isPresetCached(validPresetId)
    : false;

  // Redirect to lobby if preset is invalid (after presets have loaded)
  useEffect(() => {
    if (!isLoadingPresets && soloPresets.length > 0 && !validPresetId) {
      navigate("/solo", { replace: true });
    }
  }, [isLoadingPresets, soloPresets.length, validPresetId, navigate]);

  // Cache preset for offline use when playback starts
  useEffect(() => {
    if (isPlaying && validPresetId && !isCurrentPresetCached) {
      cachePreset(validPresetId);
    }
  }, [isPlaying, validPresetId, isCurrentPresetCached, cachePreset]);

  // Run countdown timer (visual only - playback is anchored to endTimestamp)
  useEffect(() => {
    if (status !== "countdown") return;

    const interval = setInterval(() => {
      setCountdownSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Transition to playing when countdown reaches 0 (UI state only)
  useEffect(() => {
    if (status === "countdown" && countdownSeconds <= 0) {
      setStatus("playing");
    }
  }, [status, countdownSeconds]);

  // Track when audio actually starts playing and when it ends
  useEffect(() => {
    if (isPlaying) {
      audioDidPlayRef.current = true;
      setHasAudioEnded(false);
    } else if (audioDidPlayRef.current && !isPaused) {
      // Audio finished playing naturally
      setHasAudioEnded(true);
    }
  }, [isPlaying, isPaused]);

  // Auto-start when audio is loaded
  const hasAutoStartedRef = useRef(false);

  useEffect(() => {
    if (
      status !== "idle" ||
      !validPresetId ||
      !isLoaded ||
      hasAutoStartedRef.current
    ) {
      return;
    }

    hasAutoStartedRef.current = true;

    const startSession = async () => {
      try {
        await unlockAudio();
        // Schedule playback at the exact moment the countdown display hits 0
        const endTimestamp = Date.now() + COUNTDOWN_DURATION_MS;
        schedulePlayback(endTimestamp, () => Date.now());
        setStatus("countdown");
        setCountdownSeconds(COUNTDOWN_DURATION_MS / 1000);
      } catch (error) {
        console.error("Failed to unlock audio:", error);
        hasAutoStartedRef.current = false;
        // Reset status to trigger re-attempt
        setStatus("idle");
      }
    };

    startSession();
  }, [status, validPresetId, isLoaded, unlockAudio, schedulePlayback]);

  const handleBack = useCallback(() => {
    navigate("/solo");
  }, [navigate]);

  const handleSupportAuthor = useCallback(() => {
    if (authorUrl) {
      window.open(authorUrl, "_blank", "noopener,noreferrer");
    }
  }, [authorUrl]);

  const handleStop = useCallback(() => {
    stopPlayback();
    navigate("/solo");
  }, [stopPlayback, navigate]);

  // Text based on language
  const texts =
    language === "en"
      ? {
          title: "Solo",
          loading: "Loading...",
          sessionEnd: "Remaining",
          stop: "Stop",
          pause: "Pause",
          resume: "Resume",
          paused: "Paused",
          done: "Done",
          supportAuthor: "Support Author",
        }
      : {
          title: "Соло",
          loading: "Загрузка...",
          sessionEnd: "Осталось",
          stop: "Стоп",
          pause: "Пауза",
          resume: "Продолжить",
          paused: "Пауза",
          done: "Готово",
          supportAuthor: "Поддержать автора",
        };

  // Show loading while checking preset validity
  if (isLoadingPresets || soloPresets.length === 0) {
    return (
      <div className="wrap">
        <TopBar showBack onBack={handleBack} />
        <main className="main">
          <div className="center">
            <div className="loading">
              {language === "ru" ? "Загрузка..." : "Loading..."}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="wrap">
      {status === "countdown" && countdownSeconds > 0 && (
        <CountdownOverlay seconds={countdownSeconds} language={language} />
      )}

      <TopBar showBack onBack={handleBack} />

      <main className="main">
        <div className="center">
          <header className="hdr">
            <h1>{texts.title}</h1>
            <p className="subtitle">
              {isCurrentPresetCached && (
                <span
                  className="offline-ico"
                  title={
                    language === "ru" ? "Доступен офлайн" : "Available offline"
                  }
                >
                  <OfflineIcon />
                </span>
              )}
              {presetTitle || texts.loading}
            </p>
          </header>

          <BreathingCircle isActive={isPlaying || isPaused} phase={currentPhase}>
            {/* Phase info - displayed as overlay on the circle */}
            {(isPlaying || isPaused || hasAudioEnded) && (
              <div className="circ-ovl">
                <div className="phase">
                  <span className="phase-lbl">
                    {hasAudioEnded
                      ? texts.done
                      : currentPhase
                        ? getPhaseText(currentPhase, language)
                        : ""}
                  </span>
                  <span className="phase-time">
                    {!hasAudioEnded && phaseRemaining > 0 ? formatSeconds(phaseRemaining) : ""}
                  </span>
                </div>
                {/* Support author button during outro or when finished */}
                {(currentPhase === "outro" || hasAudioEnded) && authorUrl && (
                  <button
                    className="btn btn--accent btn--lg"
                    onClick={handleSupportAuthor}
                  >
                    {texts.supportAuthor}
                  </button>
                )}
              </div>
            )}
          </BreathingCircle>

          <div className="info">
            {status === "idle" && !isLoaded && (
              <p className="wait-msg">{texts.loading}</p>
            )}

            {status === "countdown" && (
              <div className="cdown-msg">
                <button className="btn btn--danger" onClick={handleStop}>
                  {texts.stop}
                </button>
              </div>
            )}

            {(isPlaying || isPaused || hasAudioEnded) && (
              <div className="playback">
                {!hasAudioEnded && (
                  <div className="timer">
                    <span className="timer-lbl">
                      {isPaused ? texts.paused : texts.sessionEnd}
                    </span>
                    <span className="timer-val">
                      {formatSeconds(remainingTime)}
                    </span>
                  </div>
                )}
                <div className="ctrls">
                  {isPlaying && (
                    <button
                      className="btn btn--icon btn--primary"
                      onClick={pausePlayback}
                      aria-label={texts.pause}
                      title={texts.pause}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                      </svg>
                    </button>
                  )}
                  {isPaused && (
                    <button
                      className="btn btn--icon btn--accent"
                      onClick={resumePlayback}
                      aria-label={texts.resume}
                      title={texts.resume}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M8 5.14v14l11-7-11-7z" />
                      </svg>
                    </button>
                  )}
                  <button className="btn btn--danger" onClick={handleStop}>
                    {texts.stop}
                  </button>
                </div>
              </div>
            )}
          </div>

          {(status === "idle" || hasAudioEnded) && <PageFooter />}
        </div>
      </main>
    </div>
  );
}
